import React, { ForwardedRef, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Box from 'components/Box'
import Icon from 'components/Icon'
import Text from 'components/Text'

import { OptionProps, PopOverItem } from './types'

import { LabelContainer, Option as OptionContainer, SelectedMarker } from './styles'
import Avatar from 'components/Avatar'
import Radio from 'components/RadioBox'
import Flex from 'components/Flex'
import MenuList from './MenuList'
import { isEmpty } from 'lodash-es'
import Switch from 'components/Switch'
import PopOverLabel from './PopOverLabel'

const checkSelected = <T,>(option: PopOverItem<T>, selected?: PopOverItem<T> | PopOverItem<T>[]) => {
  if (Array.isArray(selected)) {
    return false
  }

  return option.value === selected?.value
}

type SelectorBaseProps = {
  children: React.ReactNode
  isDisabled?: boolean
  isFocused?: boolean
  isSelected: boolean
  showCheckbox?: boolean
  isRadio?: boolean
}

type SelectorToggleProps = {
  isToggleSwitch: true
  label: string
  textCase?: PopOverItem['textCase']
}

type SelectorNonToggleProps = {
  isToggleSwitch?: false
  label?: never
  textCase?: never
}

type SelectorProps = SelectorBaseProps & (SelectorToggleProps | SelectorNonToggleProps)

const Selector: React.FC<SelectorProps> = ({
  children,
  showCheckbox = false,
  isFocused = false,
  isSelected,
  isDisabled = false,
  isRadio = false,
  isToggleSwitch = false,
  label,
  textCase,
}) => {
  if (showCheckbox && isRadio) {
    return (
      <>
        <Box pr="8px">
          <Radio selected={isSelected} />
        </Box>
        {children}
      </>
    )
  }

  if (isToggleSwitch && label) {
    return (
      <Flex gap="0" p="8px 16px">
        <PopOverLabel label={label} textCase={textCase} overflowWrap="break-word" lineHeight="20px" />
        <Switch
          disabled={false}
          value={isSelected}
          onChange={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
        />
      </Flex>
    )
  }

  return (
    <>
      {showCheckbox && (
        <Box pr="8px">
          <Icon
            color={isDisabled ? 'blue7' : isSelected ? 'blue1' : isFocused ? 'white' : 'gray3'}
            fontSize={20}
            icon={isSelected ? 'check-square' : 'square'}
          />
        </Box>
      )}
      {children}
      {!showCheckbox && isSelected && <SelectedMarker icon="check" />}
    </>
  )
}

const Option = React.forwardRef(({ menuProps, optionProps }: OptionProps, ref: ForwardedRef<HTMLLIElement>) => {
  const { t } = useTranslation()
  const elementRef = useRef<HTMLDivElement>(null)
  const [isSubmenu, setIsSubMenu] = useState(false)
  const [topPosition, setTopPosition] = useState<string | number>(0)
  const isValidSubmenu = !isEmpty(optionProps.submenu)

  const getIcon = () => {
    if (!optionProps.icon) {
      return null
    }

    if (Array.isArray(optionProps.icon) || typeof optionProps.icon === 'string') {
      return <Icon color={optionProps.isDisabled ? 'gray4' : optionProps.iconColor} icon={optionProps.icon} mr="8px" />
    }

    return <Box mr="8px">{optionProps.icon}</Box>
  }

  const getAvatar = () => {
    if (!menuProps.showAvatar) {
      return null
    }

    return <Avatar size="xsmall" inline imgSrc={optionProps.avatar} placeholder={optionProps.avatarPlaceHolder} />
  }

  const onMouseEnter = () => {
    if (!elementRef.current) return
    const rect = elementRef.current.getBoundingClientRect()
    const containerRect = elementRef.current.offsetParent?.getBoundingClientRect()
    const relativeTop = rect.top - (containerRect?.top ?? 0)

    setTopPosition(relativeTop)
    setIsSubMenu(true)
  }

  return (
    <Box ref={elementRef} {...(isValidSubmenu ? { onMouseEnter, onMouseLeave: () => setIsSubMenu(false) } : {})}>
      {isValidSubmenu && (
        <Box display={isSubmenu ? 'block' : 'none'}>
          <MenuList
            {...menuProps}
            options={optionProps.submenu}
            isToggleSwitch={optionProps.isToggleMenu}
            isSubmenu={true}
            top={topPosition}
          />
        </Box>
      )}
      <OptionContainer
        {...optionProps}
        isSubmenu={isValidSubmenu || optionProps.parentLabel !== undefined}
        isDense={menuProps.isDense}
        isMultiple={menuProps.isMultiple}
        isSelected={menuProps.isMultiple ? false : checkSelected(optionProps, menuProps.value)}
        data-umami-event={menuProps.umamiEvent}
        data-umami-event-value={optionProps.label || optionProps.value}
        onClick={(e) => {
          e.stopPropagation()

          if (optionProps.isDisabled) return
          menuProps.onSelect(optionProps)
        }}
        ref={ref}
      >
        <Selector
          isDisabled={optionProps.isDisabled}
          isSelected={Boolean(
            Array.isArray(menuProps.value)
              ? menuProps.value?.find((v) => v.value === optionProps.value)
              : menuProps.value?.value === optionProps.value,
          )}
          showCheckbox={menuProps?.isMultiple}
          isRadio={optionProps.isRadio}
          {...(menuProps?.isToggleSwitch
            ? {
                isToggleSwitch: true as const,
                label: optionProps.label,
                textCase: optionProps.textCase,
              }
            : { isToggleSwitch: false as const })}
        >
          {optionProps.node ? (
            <LabelContainer>{optionProps.node}</LabelContainer>
          ) : (
            <Box alignItems="center" display="flex">
              {getIcon()}
              <LabelContainer showAvatar={menuProps.showAvatar} labelColor={optionProps.labelColor}>
                {getAvatar()}
                <PopOverLabel label={optionProps.label} textCase={optionProps.textCase} />
                {optionProps.addOnLabel && (
                  <Text ml={2} fontWeight="bold" color={optionProps.labelColor}>
                    ({t(optionProps.addOnLabel)})
                  </Text>
                )}
              </LabelContainer>
            </Box>
          )}
        </Selector>
        {isValidSubmenu && (
          <Flex marginLeft="auto" paddingLeft={'16px'} gap="0">
            <Icon icon={['far', 'angle-right']} color={'gray5'} fontSize="16px" />
          </Flex>
        )}
      </OptionContainer>
    </Box>
  )
})

const GroupedOption = React.forwardRef(({ menuProps, optionProps }: OptionProps, ref: ForwardedRef<HTMLLIElement>) => {
  const { t } = useTranslation()

  return (
    <>
      <OptionContainer isGroupLabel>
        {optionProps?.icon && <Icon fontSize={12} color={optionProps.iconColor} icon={optionProps.icon} mr="8px" />}
        <Text
          fontSize={12}
          fontWeight="medium"
          lineHeight="16px"
          textTransform="uppercase"
          color={optionProps.labelColor ?? 'gray5'}
        >
          {t(optionProps.label)}
          {optionProps.addOnLabel && (
            <Text ml={2} fontWeight="bold" color={optionProps.labelColor}>
              ({t(optionProps.addOnLabel)})
            </Text>
          )}
        </Text>
      </OptionContainer>
      {optionProps.options?.map((subOption: PopOverItem, idx) => (
        <Option
          key={idx}
          menuProps={menuProps}
          optionProps={{ ...menuProps, ...subOption, isIndented: true }}
          ref={ref}
        />
      ))}
    </>
  )
})

export { GroupedOption, Option }
